import { FC, ChangeEvent } from 'react';
import { Box } from '@mui/material';
import { FormikProps } from 'formik';

import styles from '../CreateIdeaStopModal/CreateIdeaStopModal.module.scss';
import { CreateIdeaStopFormData } from '../../../../../types/ideaStop.types';
import { useFormFieldRenderer } from '../CreateIdeaStopModal/hooks/useFormFieldRenderer';
import { isIdeaStopFieldMandatory } from '../../../../../types/ideaStop.config';
import CustomInput from '../../../../../common/CustomInput';
import CustomReactDatePicker from '../../../../../common/CustomReactDatePicker';
import CustomTimePicker from '../../../../../common/CustomTimePicker';
import { getMinEndTimeForSameDay } from '../../../../../utils/dateUtils';

interface OptionalFieldsProps {
  formik: FormikProps<CreateIdeaStopFormData>;
}

const OptionalFields: FC<OptionalFieldsProps> = ({ formik }) => {
  const { values, handleChange, handleBlur, setFieldTouched } = formik;
  const ideaStopType = values.ideaStopType;

  const { isFieldVisible, getError, renderFieldInput } = useFormFieldRenderer(formik, ideaStopType);

  const handleContactFieldChange = (e: ChangeEvent<HTMLInputElement>) => {
    const fieldName = e.target.name as 'email' | 'phone';
    handleChange(e);
    setFieldTouched(fieldName, true, false);
  };

  return (
    <Box className={styles.formSection}>
      {isFieldVisible('startDate') && (
        <Box className={styles.timeFields}>
          <CustomReactDatePicker
            name="startDate"
            inputLabel="Start Date"
            placeholder="Select start date"
            value={values.startDate || ''}
            onChange={handleChange}
            error={!!getError('startDate')}
            helperText={getError('startDate')}
            min={new Date().toISOString().split('T')[0]}
          />
          <CustomTimePicker
            name="startTime"
            inputLabel="Start Time"
            placeholder="Select start time"
            value={values.startTime || ''}
            onChange={handleChange}
            error={!!getError('startTime')}
            helperText={getError('startTime')}
          />
        </Box>
      )}

      {isFieldVisible('endDate') && (
        <Box className={styles.timeFields}>
          <CustomReactDatePicker
            name="endDate"
            inputLabel="End Date"
            placeholder="Select end date"
            value={values.endDate || ''}
            onChange={handleChange}
            error={!!getError('endDate')}
            helperText={getError('endDate')}
            min={values.startDate || new Date().toISOString().split('T')[0]}
          />
          <CustomTimePicker
            name="endTime"
            inputLabel="End Time"
            placeholder="Select end time"
            value={values.endTime || ''}
            onChange={handleChange}
            minTime={getMinEndTimeForSameDay(values.startDate, values.endDate, values.startTime)}
            error={!!getError('endTime')}
            helperText={getError('endTime')}
          />
        </Box>
      )}

      {isFieldVisible('checkInDate') && (
        <Box className={styles.timeFields}>
          <CustomReactDatePicker
            name="checkInDate"
            inputLabel="Check-in Date"
            placeholder="Select check-in date"
            value={values.checkInDate || ''}
            onChange={handleChange}
            error={!!getError('checkInDate')}
            helperText={getError('checkInDate')}
            min={new Date().toISOString().split('T')[0]}
          />
          <CustomTimePicker
            name="checkInTime"
            inputLabel="Check-in Time"
            placeholder="Select check-in time"
            value={values.checkInTime || ''}
            onChange={handleChange}
            error={!!getError('checkInTime')}
            helperText={getError('checkInTime')}
          />
        </Box>
      )}

      {isFieldVisible('checkOutDate') && (
        <Box className={styles.timeFields}>
          <CustomReactDatePicker
            name="checkOutDate"
            inputLabel="Check-out Date"
            placeholder="Select check-out date"
            value={values.checkOutDate || ''}
            onChange={handleChange}
            error={!!getError('checkOutDate')}
            helperText={getError('checkOutDate')}
            min={values.checkInDate || new Date().toISOString().split('T')[0]}
          />
          <CustomTimePicker
            name="checkOutTime"
            inputLabel="Check-out Time"
            placeholder="Select check-out time"
            value={values.checkOutTime || ''}
            onChange={handleChange}
            minTime={getMinEndTimeForSameDay(values.checkInDate, values.checkOutDate, values.checkInTime)}
            error={!!getError('checkOutTime')}
            helperText={getError('checkOutTime')}
          />
        </Box>
      )}

      {isFieldVisible('departureDate') && (
        <Box className={styles.timeFields}>
          <CustomReactDatePicker
            name="departureDate"
            inputLabel="Departure Date"
            placeholder="Select departure date"
            value={values.departureDate || ''}
            onChange={handleChange}
            error={!!getError('departureDate')}
            helperText={getError('departureDate')}
            min={new Date().toISOString().split('T')[0]}
          />
          <CustomTimePicker
            name="departureTime"
            inputLabel="Departure Time"
            placeholder="Select departure time"
            value={values.departureTime || ''}
            onChange={handleChange}
            error={!!getError('departureTime')}
            helperText={getError('departureTime')}
          />
        </Box>
      )}

      {isFieldVisible('arrivalDate') && (
        <Box className={styles.timeFields}>
          <CustomReactDatePicker
            name="arrivalDate"
            inputLabel="Arrival Date"
            placeholder="Select arrival date"
            value={values.arrivalDate || ''}
            onChange={handleChange}
            error={!!getError('arrivalDate')}
            helperText={getError('arrivalDate')}
            min={values.departureDate || new Date().toISOString().split('T')[0]}
          />
          <CustomTimePicker
            name="arrivalTime"
            inputLabel="Arrival Time"
            placeholder="Select arrival time"
            value={values.arrivalTime || ''}
            onChange={handleChange}
            minTime={getMinEndTimeForSameDay(
              values.departureDate,
              values.arrivalDate,
              values.departureTime
            )}
            error={!!getError('arrivalTime')}
            helperText={getError('arrivalTime')}
          />
        </Box>
      )}

      {isFieldVisible('airline') && renderFieldInput('airline', 'Airline')}

      {isFieldVisible('flightNumber') && (
        <CustomInput
          inputName="flightNumber"
          inputLabel="Flight Number"
          value={values.flightNumber || ''}
          onChange={handleChange}
          error={getError('flightNumber')}
          placeholder="e.g., FR1456, LH123"
        />
      )}

      {isFieldVisible('transportName') && (
        <CustomInput
          inputName="transportName"
          inputLabel="Transport Name"
          value={values.transportName || ''}
          onChange={handleChange}
          error={getError('transportName')}
          placeholder="e.g., Hertz, Avis, Enterprise"
        />
      )}

      {isFieldVisible('place') && !isIdeaStopFieldMandatory('place', ideaStopType) && (
        <CustomInput
          inputName="place"
          inputLabel="Place"
          value={values.place || ''}
          onChange={handleChange}
          error={getError('place')}
          placeholder="Enter place name..."
        />
      )}

      {isFieldVisible('address') && renderFieldInput('address', 'Address')}

      {isFieldVisible('departureLocation') && (
        <Box className={styles.locationFields}>
          <CustomInput
            inputName="departureLocation"
            inputLabel="Departure Location"
            value={values.departureLocation || ''}
            onChange={handleChange}
            error={getError('departureLocation')}
            placeholder="Enter departure location..."
          />
          <CustomInput
            inputName="arrivalLocation"
            inputLabel="Arrival Location"
            value={values.arrivalLocation || ''}
            onChange={handleChange}
            error={getError('arrivalLocation')}
            placeholder="Enter arrival location..."
          />
        </Box>
      )}

      {isFieldVisible('departureAddress') && (
        <Box className={styles.locationFields}>
          {renderFieldInput('departureAddress', 'Departure Address')}
          {renderFieldInput('arrivalAddress', 'Arrival Address')}
        </Box>
      )}

      {isFieldVisible('departureStation') && (
        <Box className={styles.locationFields}>
          <CustomInput
            inputName="departureStation"
            inputLabel="Departure Station"
            value={values.departureStation || ''}
            onChange={handleChange}
            error={getError('departureStation')}
            placeholder="Enter departure station..."
          />
          <CustomInput
            inputName="arrivalStation"
            inputLabel="Arrival Station"
            value={values.arrivalStation || ''}
            onChange={handleChange}
            error={getError('arrivalStation')}
            placeholder="Enter arrival station..."
          />
        </Box>
      )}

      {isFieldVisible('startingPortName') && (
        <Box className={styles.locationFields}>
          <CustomInput
            inputName="startingPortName"
            inputLabel="Starting Port"
            value={values.startingPortName || ''}
            onChange={handleChange}
            error={getError('startingPortName')}
            placeholder="Enter starting port..."
          />
          <CustomInput
            inputName="endingPortName"
            inputLabel="Ending Port"
            value={values.endingPortName || ''}
            onChange={handleChange}
            error={getError('endingPortName')}
            placeholder="Enter ending port..."
          />
        </Box>
      )}

      {isFieldVisible('startingPortAddress') && (
        <Box className={styles.locationFields}>
          {renderFieldInput('startingPortAddress', 'Starting Port Address')}
          {renderFieldInput('endingPortAddress', 'Ending Port Address')}
        </Box>
      )}

      {isFieldVisible('shipName') && (
        <CustomInput
          inputName="shipName"
          inputLabel="Ship Name"
          value={values.shipName || ''}
          onChange={handleChange}
          error={getError('shipName')}
          placeholder="Enter ship name..."
        />
      )}

      {isFieldVisible('borderCheckpoint') && (
        <CustomInput
          inputName="borderCheckpoint"
          inputLabel="Border Checkpoint"
          value={values.borderCheckpoint || ''}
          onChange={handleChange}
          error={getError('borderCheckpoint')}
          placeholder="Enter border checkpoint name..."
        />
      )}

      {isFieldVisible('email') && (
        <Box className={styles.contactFields}>
          <CustomInput
            inputName="email"
            inputLabel="Email"
            inputType="email"
            value={values.email || ''}
            onChange={handleContactFieldChange}
            onBlur={handleBlur}
            error={getError('email')}
            placeholder="Enter email address..."
          />
          <CustomInput
            inputName="phone"
            inputLabel="Phone"
            value={values.phone || ''}
            onChange={handleContactFieldChange}
            onBlur={handleBlur}
            error={getError('phone')}
            placeholder="Enter phone number..."
          />
        </Box>
      )}

      {isFieldVisible('link') && (
        <CustomInput
          inputName="link"
          inputLabel="Link"
          value={values.link || ''}
          onChange={handleChange}
          error={getError('link')}
          placeholder="https://..."
        />
      )}

      {isFieldVisible('notes') && (
        <CustomInput
          inputName="notes"
          inputLabel="Notes"
          value={values.notes || ''}
          onChange={handleChange}
          error={getError('notes')}
          placeholder="Add any additional notes..."
        />
      )}
    </Box>
  );
};

export default OptionalFields;
